import { useContext, useMemo } from "react";
import { BrPageContext, BrComponentContext } from "@bloomreach/react-sdk";
import NavigationContext from "./context";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import { DesktopNav, MobileNav } from "../../media-queries/MediaQueries";
import DesktopNavigation from "./DesktopNavigation";
import MobileNavigation from "./MobileNavigation";
import { getProperties } from "../../helpers/GlobalHelpers";
import useProductCache from "../../hooks/useProductCache";
import "./Navigation.scss";

const GLOBAL_CONFIG_HEADER_COMPONENT_NAME = "Global Header";

function Navigation() {
  const page = useContext(BrPageContext);
  const component = useContext(BrComponentContext);
  const { popularProductCodes, productServletURL, flyoutPopularProductCodes } =
    component.getModels("header");

  // Memoize product codes to prevent array recreation on every render
  const memoizedPopularCodes = useMemo(() => popularProductCodes || [], [
    JSON.stringify(popularProductCodes)
  ]);
  
  const memoizedFlyoutCodes = useMemo(() => flyoutPopularProductCodes || [], [
    JSON.stringify(flyoutPopularProductCodes)
  ]);

  // Fetch popular products with caching
  const { products: popularProducts = [] } = useProductCache(
    memoizedPopularCodes,
    productServletURL,
    null,
    null,
    !!(memoizedPopularCodes.length && productServletURL)
  );

  // Fetch flyout popular products with caching
  const { products: flyoutPopularProducts = [] } = useProductCache(
    memoizedFlyoutCodes,
    productServletURL,
    null,
    null,
    !!(memoizedFlyoutCodes.length && productServletURL)
  );

  try {
    const { document: documentRef, hideComponent } = component.getModels();
    const document = page.getContent(documentRef);
    if (!document) return null;
    if (hideComponent) return null;

    const value = getProperties(component)(GLOBAL_CONFIG_HEADER_COMPONENT_NAME);

    const newFlyouts = {};
    if (flyoutPopularProducts && Array.isArray(flyoutPopularProducts)) {
      flyoutPopularProducts.forEach((p) => {
        newFlyouts[p.productCode] = p;
      });
    }

    value.popularProducts = popularProducts || [];
    value.flyoutPopularProducts = newFlyouts;

    return (
      <header className={`${page.isPreview() ? "has-edit-button" : ""} navigation`}>
        <div className="navigation__wrapper">
          <NavigationContext.Provider value={value}>
            <DesktopNav>
              <DesktopNavigation navDocument={document} />
            </DesktopNav>
            <MobileNav>
              <MobileNavigation navDocument={document} />
            </MobileNav>
          </NavigationContext.Provider>
        </div>
      </header>
    );
  } catch (e) {
    console.error("Navigation error", e);
    return <ErrorComponent error={e} componentName={"Navigation"} />;
  }
}

export default Navigation;
